import React, { useState, useEffect } from 'react';
import { 
  Lock, 
  Unlock, 
  Calendar, 
  AlertCircle, 
  CheckCircle2, 
  Clock, 
  ArrowRight,
  ShieldCheck,
  LayoutDashboard,
  RefreshCw,
  AlertTriangle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import API_BASE_URL from '../Config';

export default function CierreDiario() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [fechaProceso, setFechaProceso] = useState(null);
  const [stats, setStats] = useState({
    cajasAbiertas: 0,
    transferenciasPendientes: 0,
    transaccionesHoy: 0
  });
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch Fecha Proceso
      const resFecha = await fetch(`${API_BASE_URL}/tesoreria/cierre-diario/fecha-proceso`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resFecha.ok) {
        const data = await resFecha.json();
        setFechaProceso(data);
      }

      // Fetch Stats (Mocked for now, until specific endpoints exist)
      // In a real scenario, we'd have a dashboard endpoint for EOD
      setStats({
        cajasAbiertas: 0, // Should come from backend
        transferenciasPendientes: 0,
        transaccionesHoy: 25
      });

    } catch (err) {
      console.error("Error fetching EOD data", err);
      setError("No se pudo conectar con el servidor de tesorería.");
    } finally {
      setLoading(false);
    }
  };

  const handleEjecutarCierre = async () => {
    if (!window.confirm(`¿Está seguro de cerrar el día financiero ${fechaProceso}? Esta acción es irreversible.`)) {
      return;
    }

    try {
      setClosing(true);
      setError(null);
      const res = await fetch(`${API_BASE_URL}/tesoreria/cierre-diario/ejecutar?usuarioId=${user.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
          fetchData();
        }, 3000);
      } else {
        const errorData = await res.text();
        setError(errorData || "Error al ejecutar el cierre diario.");
      }
    } catch (err) {
      setError("Error de red al intentar cerrar el día.");
    } finally {
      setClosing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-600 rounded-lg">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-black text-slate-900 tracking-tight">Cierre de Día Financiero</h1>
          </div>
          <p className="text-slate-500 font-bold uppercase text-[10px] tracking-widest flex items-center gap-2">
            Control de Integridad Contable <ArrowRight className="w-3 h-3" /> EOD Processing
          </p>
        </div>

        <div className="bg-white px-6 py-4 rounded-3xl border-2 border-slate-900 shadow-[8px_8px_0px_0px_rgba(15,23,42,1)] flex items-center gap-4">
          <Calendar className="w-6 h-6 text-blue-600" />
          <div>
            <p className="text-[10px] font-black text-slate-400 uppercase">Día en Proceso</p>
            <p className="text-xl font-black text-slate-900">{fechaProceso}</p>
          </div>
        </div>
      </div>

      {/* Main Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Left Column: Requirements */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-[2rem] border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden">
            <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h2 className="text-lg font-black text-slate-800 flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                Pre-requisitos de Cierre
              </h2>
              <span className="px-3 py-1 bg-amber-100 text-amber-700 text-[10px] font-black rounded-full uppercase tracking-wider">
                Verificación Obligatoria
              </span>
            </div>
            
            <div className="p-8 space-y-6">
              {[
                { 
                  title: "Cajas de Ventanilla Cerradas", 
                  desc: "Todos los cajeros deben haber realizado su arqueo y cierre individual.",
                  status: stats.cajasAbiertas === 0,
                  value: `${stats.cajasAbiertas} abiertas`
                },
                { 
                  title: "Transferencias de Bóveda Finalizadas", 
                  desc: "No deben existir solicitudes de provisión o retiro pendientes de aceptación.",
                  status: stats.transferenciasPendientes === 0,
                  value: `${stats.transferenciasPendientes} pendientes`
                },
                { 
                  title: "Integridad Contable", 
                  desc: "Verificación de cuadratura entre transacciones de caja y asientos contables.",
                  status: true,
                  value: "Cuadrado"
                }
              ].map((req, i) => (
                <div key={i} className="flex items-start gap-4 p-5 rounded-2xl border border-slate-100 bg-slate-50/30">
                  <div className={`mt-1 w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${req.status ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>
                    {req.status ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-1">
                      <h3 className="font-bold text-slate-800">{req.title}</h3>
                      <span className={`text-[10px] font-black px-2 py-0.5 rounded-md ${req.status ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                        {req.value}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 leading-relaxed">{req.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border-2 border-red-200 p-6 rounded-3xl flex items-start gap-4 animate-shake">
              <AlertCircle className="w-6 h-6 text-red-600 shrink-0" />
              <div>
                <h4 className="font-black text-red-900 uppercase text-xs tracking-widest mb-1">Error de Bloqueo</h4>
                <p className="text-red-700 text-sm font-medium">{error}</p>
              </div>
            </div>
          )}

          {success && (
            <div className="bg-emerald-50 border-2 border-emerald-200 p-6 rounded-3xl flex items-start gap-4 animate-bounce-short">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
              <div>
                <h4 className="font-black text-emerald-900 uppercase text-xs tracking-widest mb-1">Cierre Exitoso</h4>
                <p className="text-emerald-700 text-sm font-medium">El día ha sido cerrado y la fecha de proceso se ha actualizado correctamente.</p>
              </div>
            </div>
          )}
        </div>

        {/* Right Column: Action */}
        <div className="space-y-6">
          <div className="bg-slate-900 rounded-[2rem] p-8 text-white shadow-2xl relative overflow-hidden">
            <div className="relative z-10">
              <h2 className="text-xl font-black mb-4">Ejecutar Cierre</h2>
              <p className="text-slate-400 text-sm mb-8 leading-relaxed">
                Al ejecutar el cierre, el sistema avanzará a la siguiente fecha hábil y generará el asiento de cierre de ingresos/egresos del día.
              </p>
              
              <button
                onClick={handleEjecutarCierre}
                disabled={closing || stats.cajasAbiertas > 0}
                className={`w-full py-5 rounded-2xl font-black text-lg flex items-center justify-center gap-3 transition-all shadow-xl ${
                  closing || stats.cajasAbiertas > 0
                  ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-500 text-white shadow-blue-900/40 hover:-translate-y-1'
                }`}
              >
                {closing ? (
                  <RefreshCw className="w-6 h-6 animate-spin" />
                ) : (
                  <>
                    <Lock className="w-6 h-6" />
                    <span>CERRAR DÍA</span>
                  </>
                )}
              </button>

              <div className="mt-8 pt-8 border-t border-white/10 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Estado Sistema</span>
                  <span className="flex items-center gap-2 text-emerald-400 text-[10px] font-black uppercase">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                    Online
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Transacciones</span>
                  <span className="text-white text-xs font-black">{stats.transaccionesHoy}</span>
                </div>
              </div>
            </div>
            
            {/* Background design elements */}
            <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-64 h-64 bg-blue-600/10 rounded-full blur-3xl"></div>
          </div>

          <div className="bg-white rounded-[2rem] border border-slate-200 p-8">
            <h3 className="font-black text-slate-800 mb-4 flex items-center gap-2 uppercase text-[10px] tracking-widest">
              <Clock className="w-4 h-4 text-slate-400" />
              Historial Reciente
            </h3>
            <div className="space-y-4">
              {[1, 2].map(i => (
                <div key={i} className="flex items-center justify-between py-3 border-b border-slate-50 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                      <Lock className="w-4 h-4 text-slate-400" />
                    </div>
                    <div>
                      <p className="text-xs font-black text-slate-700">2026-05-0{4-i}</p>
                      <p className="text-[9px] text-slate-400 font-bold uppercase tracking-tighter">Cerrado por: Administrador</p>
                    </div>
                  </div>
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
